import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useChatStore } from '../stores/chatStore';
import { useAuthStore } from '../stores/authStore';
import { chatService } from '../services/chatService';
import { fileService } from '../services/fileService';
import { variantConfirmationService } from '../services/confirmVarService';
import { websocketService, FileStatusUpdate } from '../services/websocketService';
import { useNetworkStatus } from './useNetworkStatus';
import type { StepItem } from '../types';

// WS 文件状态 → 步骤四色状态（蓝=进行中 / 绿=完成 / 橙=需关注 / 红=失败）
function stepStatusFromUpdate(update?: FileStatusUpdate): StepItem['status'] {
    const s = update?.status || '';
    if (s === 'failed') return 'error';
    if (s === 'needs_review') return 'warning';
    if (s === 'completed' || s === 'ntrk_analysis_completed' || s === 'all_files_ready') return 'done';
    return 'active';
}

// 文件状态接口定义
interface FileStatusSummary {
  total: number;
  confirmed: number;
  analyzed: number;
  chat_ready: boolean;
  details?: Array<{
    id: string;
    filename: string;
    status: string;
    status_display: string;
    analysis_progress: number;
  }>;
}

export const useChat = () => {
    const {
        messages, isTyping, files, currentSessionId, chatSessions,
        isLoadingSessions, setMessages, addMessage, updateMessage, appendMessageStep, appendMessageText,
        setIsTyping, addFiles, removeFile, clearFiles,
        setCurrentSessionId, setChatSessions, setIsLoadingSessions,
        updateSessionName, clearChat,
    } = useChatStore();

    const { user, isAuthenticated, accessToken } = useAuthStore();
    const { isOnline } = useNetworkStatus();
    
    // 状态管理
    const [processingFileIds, setProcessingFileIds] = useState<string[]>([]);
    const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting' | 'error'>('disconnected');
    const [pendingUserPrompt, setPendingUserPrompt] = useState<string | null>(null); // 新增状态：等待文件解析完成的用户提示语
    const [fileStatus, setFileStatus] = useState<FileStatusSummary | null>(null);
    const [isChatReady, setIsChatReady] = useState(false);
    // 使用 Ref 追踪最新的 files 引用，用于清理函数
    const filesRef = useRef(files);
    useEffect(() => { filesRef.current = files; }, [files]);
    // 多文件协调：记录本次待分析的文件数，全部完成（或收到 all_files_ready）后才触发流式回答
    const processingFileIdsRef = useRef<string[]>([]);
    const detVerdictRef = useRef<string | null>(null);
    const pendingFileCountRef = useRef(0);
    // 流式回答是否已触发的同步标志：防止 ntrk_analysis_completed 与
    // all_files_ready 两条紧邻的 WS 消息在同一闭包内重复触发流式回答
    const streamTriggeredRef = useRef(false);
    // 已由各流程认领的 file_id 集合：上传流程 / 文本报告流程 / 手动录入各自
    // 负责挂载自己的确认卡片，常驻 WS 订阅据此避免重复挂载
    const handledFileIdsRef = useRef<Set<string>>(new Set());
    // 确认完成后触发流式分析时要关联到用户消息的文件 ID：
    // 后端据此把文件挂到消息上，历史记录重新加载时才能恢复显示附件
    const pendingAnalysisFileIdsRef = useRef<string[]>([]);
    // 对话式确认状态（替代原确认卡片）：当前待确认文件的数据草稿与缺失项
    const pendingConfirmRef = useRef<{
        fileId: string; fileName: string;
        variants: import('../types/confirmVar').VariantData[];
        tumorType: string; coVariants: string;
        missing: string[]; awaitingSkip: boolean; manualEntry?: boolean;
    } | null>(null);
    // 多文件上传时待确认文件排队
    const confirmQueueRef = useRef<Array<{ id: string; name: string; status: string }>>([]);
    // 标记第一阶段（解析/核对）是否已结束：只有确认后进入 Phase 2 的 'failed'
    // 才应提示"深度分析失败"；Phase 1 的失败由内联确认卡片承接（手动补录）
    const phase1CompletedRef = useRef(false);
    // 已打印过提取结果快照的文件 ID（自动确认模式只读展示，同一文件仅打印一次）
    const printedExtractionsRef = useRef<Set<string>>(new Set());

    // --- 1. 修复：响应式 WebSocket 状态监听 ---
    
    useEffect(() => {
        
        const unsubscribe = websocketService.onStateChange((state) => {
            if (state.isConnected) {
                setConnectionStatus('connected');
            } else if (state.isConnecting) {
                setConnectionStatus('connecting');
            } else {
                setConnectionStatus(isOnline ? 'disconnected' : 'error');
            }
        });
        return () => {
            unsubscribe(); // 显式执行，不把它的返回值传给 React
        }; // 组件卸载时取消订阅
    }, [isOnline]);

    useEffect(() => {
        // 只有当用户已认证且有 AccessToken 时才尝试连接
        if (isAuthenticated && accessToken) {
            console.log('🚀 Authenticated, initiating WebSocket...');
            websocketService.forceReconnect(accessToken);
            websocketService.enableAutoConnect(true);
        } else {
            // 如果用户登出或未认证，彻底关闭连接
            console.log('🛑 Unauthenticated, closing WebSocket...');
            websocketService.close();
        }

        // 组件卸载时，为了安全也关闭连接
        return () => {
            websocketService.close();
        };
    }, [isAuthenticated, accessToken]); // 监听这两个关键变量
    
    
   
    // 辅助：获取认证字符串
    const authString = useMemo(() => {
        if (accessToken) return `Bearer ${accessToken}`;
        if (user?.authString) return user.authString.startsWith('Basic ') ? user.authString : `Basic ${user.authString}`;
        return null;
    }, [accessToken, user?.authString]);

    // ✅ 纯文字聊天走 HTTP SSE 流式接口，不依赖 WebSocket；
    // WebSocket 仅用于文件解析进度推送（且有 HTTP 轮询兜底），
    // 因此不把 WS 连接状态作为发送前提，避免 WS 故障时输入"无任何反应"。
    const canSendMessage = useMemo(() =>
        !isTyping && isOnline,
    [isTyping, isOnline]);

    const hasMessages = useMemo(() => messages.length > 1, [messages.length]);

    // --- 2. 修复：带有内存释放功能的内部方法 ---
    
    const handleRemoveFile = useCallback((id: number) => {
        const fileToRemove = files.find(f => f.id === id);
        if (fileToRemove?.preview) {
            URL.revokeObjectURL(fileToRemove.preview); // 释放内存
        }
        removeFile(id);
    }, [files, removeFile]);

    const handleClearFiles = useCallback(() => {
        files.forEach(f => {
            if (f.preview) URL.revokeObjectURL(f.preview); // 批量释放
        });
        clearFiles();
    }, [files, clearFiles]);

    const handleFileSelect = useCallback((selectedFiles: File[]) => {
        addFiles(selectedFiles.map(file => ({
            id: Date.now() + Math.random(), // 避免 ID 碰撞
            file, 
            name: file.name, 
            size: file.size, 
            type: file.type,
            preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        })));
    }, [addFiles]);

    // --- 3. 消息发送逻辑 ---

   

    const updateAiMessage = useCallback((id: number, text: string) => {
        // 移除 messages 依赖，只管发指令给 store
        updateMessage(id, { text });
    }, [updateMessage]); // 仅依赖 action

    // 向消息追加一条后台处理步骤（对齐离线版 thinking-chain：只增不覆盖）。
    // 相邻去重：同一文件同文同状态的周期重发（轮询/WS 重复推送）不入列。
    const appendProcessStep = useCallback((messageId: number, label: string, update?: FileStatusUpdate) => {
        let status = stepStatusFromUpdate(update);
        // 无原始更新对象的合成标签（waitForFilesProcessing 的结束语）按文案定色
        if (!update) {
            if (/已取消|分析失败/.test(label)) status = 'error';
            else if (/解析部分完成/.test(label)) status = 'warning';
            else if (/解析完成|上传完成/.test(label)) status = 'done';
            else if (/耗时较长|后台仍在处理/.test(label)) status = 'warning';
        }
        const text = (update?.status_message || label || '').trim();
        if (!text) return;
        const cur = useChatStore.getState().messages.find(m => m.id === messageId);
        const last = cur?.steps?.[cur.steps.length - 1];
        // 2026-09-18 用户实测步骤重复：同文同态即折叠——合成标签（上传即时亮/轮询兜底）
        // 与 WS 事件同文案但无 _key，旧逻辑要求 _key 相等导致折叠失效。
        // file_id 双侧都有时才比对（多文件同文案不被误折叠），单侧缺失视为同源折叠
        const dedupKey = `${update?.file_id || ''}|${text}|${status}`;
        const lastFile = ((last as StepItem & { _key?: string })?._key || '').split('|')[0];
        const thisFile = update?.file_id || '';
        if (last && last.text === text && last.status === status &&
            (!lastFile || !thisFile || lastFile === thisFile)) return;
        const detailParts = [`状态码：${update?.status || '-'}`];
        if (update?.file_name) detailParts.push(`文件：${update.file_name}`);
        if (update?.status_message && update.status_message !== text)
            detailParts.push(`原始消息：${update.status_message}`);
        appendMessageStep(messageId, {
            text,
            detail: update ? detailParts.join('\n') : undefined,
            status,
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
            ...(update ? { _key: dedupKey } : {}),
        } as Omit<StepItem, 'seq'>);
    }, [appendMessageStep]);
    const createChatSession = useCallback(async () => {
        try {
            const session = await chatService.createSession({ sessionName: '新对话' }, authString || undefined);
            setCurrentSessionId(session.id);
            return session.id;
        } catch (error) {
            return null;
        }
    }, [authString, setCurrentSessionId]);

    // ================= 对话式变异确认（替代原内联卡片） =================
    // 提取完成 → AI 文字摘要（含缺失项提示）→ 用户回复「确认」/补充文字
    // → LLM 合并补充 → 信息不足时追问 → 确认后进入 Phase 2

    const CONFIRM_RE = /^(确认|对|对的|正确|是的|没问题|可以|好的?|ok|OK)$/;
    const NEGATIVE_RE = /^(阴性|未检出|没有变异|无变异|报告阴性)$/;
    const SKIP_RE = /^(无|没有|未提及|不知道|跳过|不详|不清楚)$/;

    const filledVariants = useCallback(() => {
        return (pendingConfirmRef.current?.variants || []).filter(
            (v) => (v.ntrk_variant || '').trim() || (v.breakpoint || '').trim() || (v.abundance || '').trim()
        );
    }, []);

    // 提取结果只读打印（自动确认模式，对齐小程序 printExtractionResult）：
    // 后端随 ntrk_analysis 推送 extraction_result 快照，前端直接展示，
    // 替代原对话式确认环节；同一文件只打印一次
    const printExtractionSummary = useCallback((ex: NonNullable<import('../services/websocketService').FileStatusUpdate['extraction_result']>, fileId: string) => {
        if (!ex || !fileId || printedExtractionsRef.current.has(fileId)) return;
        printedExtractionsRef.current.add(fileId);
        const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        const variants = ex.variants || [];
        let body: string;
        if (variants.length === 0) {
            body = '未检出明确的 NTRK 变异（阴性结果）';
        } else {
            body = variants.map((v, i) => {
                const parts = [
                    v.breakpoint ? `断点 ${v.breakpoint}` : null,
                    v.abundance ? `丰度 ${v.abundance}` : null,
                    v.partner_gene ? `伴侣 ${v.partner_gene}` : null,
                    v.ntrk_transcript_info ? `转录本 ${v.ntrk_transcript_info}` : null,
                ].filter(Boolean).join('，');
                return `${i + 1}. ${v.ntrk_variant || '(未命名变异)'}${parts ? '（' + parts + '）' : ''}`;
            }).join('\n');
        }

        const clinic = [
            ex.tumor_type ? `癌种：${ex.tumor_type}` : null,
            ex.co_variants ? `伴随变异：${ex.co_variants}` : null,
        ].filter(Boolean).join('\n');

        let text = `📋 「${ex.file_name || '检测报告'}」提取结果：\n\n${body}`;
        if (clinic) text += `\n${clinic}`;
        text += '\n\n🔄 已自动进入后续分析，请稍候...';

        addMessage({ id: Date.now(), text, sender: 'ai', timestamp, liked: null });
    }, [addMessage]);

    // 生成确认摘要消息
    const renderConfirmSummary = useCallback(() => {
        const pc = pendingConfirmRef.current;
        if (!pc) return;
        const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        let body: string;
        if (pc.variants.length === 0 && !pc.manualEntry) {
            body = '未检出明确的 NTRK 变异（阴性结果）';
        } else if (pc.variants.length === 0) {
            body = '（暂未录入变异数据）';
        } else {
            body = pc.variants.map((v, i) => {
                const parts = [
                    v.breakpoint ? `断点 ${v.breakpoint}` : null,
                    v.abundance ? `丰度 ${v.abundance}` : null,
                    v.partner_gene ? `伴侣 ${v.partner_gene}` : null,
                    v.ntrk_transcript_info ? `转录本 ${v.ntrk_transcript_info}` : null,
                ].filter(Boolean).join('，');
                return `${i + 1}. ${v.ntrk_variant || '(未命名变异)'}${parts ? '（' + parts + '）' : ''}`;
            }).join('\n');
        }

        const clinic = [
            pc.tumorType ? `癌种：${pc.tumorType}` : null,
            pc.coVariants ? `伴随变异：${pc.coVariants}` : null,
        ].filter(Boolean).join('\n');

        let text = `📋 「${pc.fileName}」的变异信息如下，请核对：\n\n${body}`;
        if (clinic) text += `\n${clinic}`;

        if (pc.missing.length > 0) {
            text += `\n\n⚠️ 以下信息不足：${pc.missing.join('、')}。\n请补充后直接发送（如：断点 E5:E15；癌种：肺癌）；若报告未提及，回复「无」即可继续。`;
            text += '\n\n✅ 信息无误且已补齐 → 回复「确认」开始深度分析';
        } else {
            text += '\n\n✅ 信息无误 → 回复「确认」开始深度分析';
        }
        text += '\n✏️ 需要修正/补充 → 直接发送内容\n（阴性报告 → 回复「阴性」）';

        addMessage({ id: Date.now(), text, sender: 'ai', timestamp, liked: null });
    }, [addMessage]);

    // 拉取待确认数据并进入对话式确认
    const loadPendingConfirm = useCallback(async (fileId: string, fileName: string, _status: string) => {
        try {
            const data = await variantConfirmationService.getPendingVariants(fileId, authString || undefined);
            pendingConfirmRef.current = {
                fileId,
                fileName: fileName || data.file_name || '检测报告',
                variants: data.variants || [],
                tumorType: data.tumor_type || '',
                coVariants: data.co_variants || '',
                missing: data.missing_fields || [],
                awaitingSkip: false,
                manualEntry: false,
            };
            renderConfirmSummary();
        } catch (e) {
            addMessage({
                id: Date.now(), sender: 'ai', liked: null,
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                text: `❌ 提取结果加载失败：${e instanceof Error ? e.message : '未知错误'}，您也可以直接发送变异信息进行手动录入。`,
            });
        }
    }, [authString, addMessage, renderConfirmSummary]);

    // 多文件队列：处理下一个待确认文件
    const startNextConfirm = useCallback(() => {
        const next = confirmQueueRef.current.shift();
        if (next) loadPendingConfirm(next.id, next.name, next.status);
    }, [loadPendingConfirm]);

    const doConfirm = useCallback(async (force: boolean) => {
        const pc = pendingConfirmRef.current;
        if (!pc || !authString) return;
        const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const aiId = Date.now();
        addMessage({ id: aiId, text: '✅ 数据已确认，正在进行 NTRK 深度分析，请稍候...', sender: 'ai', timestamp, liked: null });
        try {
            const res = await variantConfirmationService.confirmVariants(
                pc.fileId, filledVariants(), pc.tumorType, pc.coVariants, authString, force
            );
            if (res.insufficient) {
                // 后端判定仍不足：更新缺失项并继续追问
                pendingConfirmRef.current = { ...pc, missing: res.missing_fields || [], awaitingSkip: true };
                updateMessage(aiId, { text: `⚠️ ${res.message}` });
                return;
            }
            pendingConfirmRef.current = null;
            window.dispatchEvent(new CustomEvent('variant-confirmation-success', { detail: { fileId: pc.fileId } }));
            // Phase 2 完成推送会驱动后续流式回答（pendingUserPrompt 效果）
            startNextConfirm();
        } catch (e) {
            updateMessage(aiId, { text: `❌ 确认提交失败：${e instanceof Error ? e.message : '未知错误'}，请回复「确认」重试。` });
        }
    }, [authString, addMessage, updateMessage, filledVariants, startNextConfirm]);

    const doConfirmNegative = useCallback(async () => {
        const pc = pendingConfirmRef.current;
        if (!pc || !authString) return;
        const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const aiId = Date.now();
        addMessage({ id: aiId, text: '✅ 已按阴性报告确认，正在进行分析...', sender: 'ai', timestamp, liked: null });
        try {
            await variantConfirmationService.confirmVariants(pc.fileId, [], pc.tumorType, pc.coVariants, authString);
            pendingConfirmRef.current = null;
            window.dispatchEvent(new CustomEvent('variant-confirmation-success', { detail: { fileId: pc.fileId } }));
            startNextConfirm();
        } catch (e) {
            updateMessage(aiId, { text: `❌ 确认提交失败：${e instanceof Error ? e.message : '未知错误'}，请回复「阴性」重试。` });
        }
    }, [authString, addMessage, updateMessage, startNextConfirm]);

    // 确认阶段的文字回复路由（由 sendMessage 调用）
    const handleConfirmReply = useCallback(async (text: string) => {
        const pc = pendingConfirmRef.current;
        if (!pc) return;
        const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        // 1. 确认
        if (CONFIRM_RE.test(text)) {
            if (pc.missing.length > 0 && !pc.awaitingSkip) {
                pendingConfirmRef.current = { ...pc, awaitingSkip: true };
                addMessage({
                    id: Date.now(), sender: 'ai', timestamp, liked: null,
                    text: `⚠️ 还缺少：${pc.missing.join('、')}。\n请补充后直接发送；若报告确实未提及，回复「无」即可继续分析。`,
                });
                return;
            }
            await doConfirm(false);
            return;
        }

        // 2. 跳过缺失项
        if (SKIP_RE.test(text) && pc.missing.length > 0) {
            await doConfirm(true);
            return;
        }

        // 3. 阴性报告
        if (NEGATIVE_RE.test(text)) {
            await doConfirmNegative();
            return;
        }

        // 4. 补充/修正说明 → LLM 合并
        const aiId = Date.now();
        addMessage({ id: aiId, text: '🔄 正在合并补充信息...', sender: 'ai', timestamp, liked: null });
        try {
            const data = await variantConfirmationService.refineVariants(pc.fileId, text, authString || undefined);
            pendingConfirmRef.current = {
                ...pc,
                variants: data.variants || [],
                tumorType: data.tumor_type || pc.tumorType,
                coVariants: data.co_variants || pc.coVariants,
                missing: data.missing_fields || [],
                awaitingSkip: false,
            };
            updateMessage(aiId, { text: '' });
            renderConfirmSummary();
        } catch (e) {
            updateMessage(aiId, { text: `❌ 补充信息合并失败：${e instanceof Error ? e.message : '未知错误'}\n请换个表述重试，或回复「确认」使用当前数据。` });
        }
    }, [authString, addMessage, updateMessage, doConfirm, doConfirmNegative, renderConfirmSummary,
        CONFIRM_RE, NEGATIVE_RE, SKIP_RE]);

    const sendMessage = useCallback(async (messageText: string) => {
        // 1. 基础校验
        if (messageText.trim() === '' && files.length === 0) return;
        if (!canSendMessage) {
            // 抛出异常让 InputArea 恢复输入框内容，避免用户输入被静默吞掉
            throw new Error(isTyping ? '上一条消息仍在处理中，请稍候再发送' : '网络连接不可用，请检查网络后重试');
        }

        const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        // 对话式确认进行中：纯文字输入优先路由到确认/补充处理
        if (pendingConfirmRef.current && files.length === 0) {
            addMessage({ id: Date.now(), text: messageText, sender: 'user', timestamp });
            await handleConfirmReply(messageText.trim());
            return;
        }

        const finalPrompt = messageText.trim() !== ''
        ? messageText
        : "请分析此报告中的 NTRK 变异情况。";

        let sessionId = currentSessionId;

        // 2. UI 乐观更新（先展示气泡，后续任何失败都会在气泡中给出反馈，避免"毫无反应"）
        const filesToUpload = [...files];
        const userMsgId = Date.now();

        addMessage({ id: userMsgId, text: messageText, sender: 'user', timestamp, files: filesToUpload.length > 0 ? filesToUpload : undefined });
        clearFiles();

        const aiMessageId = userMsgId + 1;
        addMessage({ id: aiMessageId, text: '正在准备...', sender: 'ai', timestamp, liked: null });
        setIsTyping(true);

        // 用于文件处理的超时处理
        let fileProcessingTimeout: NodeJS.Timeout | null = null;
        let isFileProcessing = false;

        try {
            if (!isAuthenticated || !authString) {
                throw new Error('认证信息失效，请重新登录');
            }

            // 3. 确保会话存在
            if (!sessionId) {
                sessionId = await createChatSession();
                if (!sessionId) throw new Error('无法创建聊天会话，请稍后重试');
            }

            // --- 场景 A: 包含文件上传 (第一阶段：解析) ---
            if (filesToUpload.length > 0) {
                isFileProcessing = true;
                phase1CompletedRef.current = false;
                streamTriggeredRef.current = false;
                setPendingUserPrompt(finalPrompt);
                // 状态框接管该气泡：正文清空，步骤逐条追加（对齐离线版，不覆盖历史）
                updateMessage(aiMessageId, { text: '' });
                // 友好性③：取消按钮（取消进行中的分析；后端协作式终止链内子进程）
                updateMessage(aiMessageId, {
                    onCancel: () => {
                        processingFileIdsRef.current.forEach(fid => {
                            fileService.cancelFile(fid, authString!).catch(e => console.warn('取消失败:', e));
                        });
                        updateMessage(aiMessageId, { notice: '取消中…' });
                    }
                });
                // 2026-09-18 对齐生产首段播报（ntrk_pipeline._push 逐字）：
                // PDF → 读取报告识别变异页；图片 → 识别报告图片内容
                const hasPdf = filesToUpload.some(f => /\.pdf$/i.test(f.name) || f.type === 'application/pdf');
                appendProcessStep(aiMessageId, hasPdf
                    ? '🔬 正在读取报告内容，识别 NTRK 变异相关页……'
                    : '🔬 正在识别报告图片内容……');
                const backendFileIds = await fileService.uploadMultipleFiles(filesToUpload, sessionId, authString);
                setProcessingFileIds(backendFileIds);
                processingFileIdsRef.current = backendFileIds;
                pendingFileCountRef.current = backendFileIds.length;
                // 认领这批文件：确认卡片由本流程（场景 A）负责挂载
                backendFileIds.forEach(id => handledFileIdsRef.current.add(id));
                // 确认后触发流式分析时，将这些文件关联到用户消息（历史记录可恢复附件显示）
                pendingAnalysisFileIdsRef.current = backendFileIds;

                // 设置文件处理超时提示（显示为步骤框下方的提示条，不打断步骤流）
                fileProcessingTimeout = setTimeout(() => {
                    updateMessage(aiMessageId, { notice: '文件处理中，请稍候...如长时间无响应，请检查网络连接。' });
                }, 60000*5); // 5分钟

                // 等待解析（无论成功失败，只要 ready 就会 resolve）
                // ✅ 关键：传递 session_id 给 WebSocket 订阅
                await fileService.waitForFilesProcessing(
                    backendFileIds,
                    authString,
                    (msg, update) => appendProcessStep(aiMessageId, msg, update),
                    sessionId,  // ✅ 传递 session_id
                    15          // 全局等待上限 15 分钟：NGS 报告解析+深度分析常超 3 分钟默认值
                );

                // 清除超时提示
                if (fileProcessingTimeout) {
                    clearTimeout(fileProcessingTimeout);
                    fileProcessingTimeout = null;
                }
                updateMessage(aiMessageId, { notice: '' });

                // 标记第一阶段结束：此后的 'failed' 均为 Phase 2 深度分析失败
                phase1CompletedRef.current = true;

                // 查询各文件终态，需要人工核对的文件生成内联确认卡片
                // 自动确认模式（默认）下 to_be_confirmed 只是过渡态：后台任务会自动
                // 确认并进入 Phase 2，前端跳过确认交互（提取结果快照由 WS 推送打印）；
                // 仅当服务端关闭自动确认（回滚）时才把 to_be_confirmed 纳入待确认列表
                const NEEDS_CONFIRMATION = ['failed', 'needs_review'];
                let autoConfirmOn = false;
                const confirmResults = await Promise.all(
                    backendFileIds.map(async (fid, idx) => {
                        try {
                            const st = await fileService.getFileStatus(fid, authString);
                            if (st.auto_confirm !== false) autoConfirmOn = true;
                            const needConfirm = NEEDS_CONFIRMATION.includes(st.status)
                                || (st.status === 'to_be_confirmed' && st.auto_confirm === false);
                            if (needConfirm) {
                                return { id: fid, name: filesToUpload[idx]?.name || '检测报告', status: st.status };
                            }
                        } catch { /* 状态查询失败的文件不阻塞流程 */ }
                        return null;
                    })
                );
                const filesNeedingConfirmation = confirmResults.filter((f): f is NonNullable<typeof f> => f !== null);

                // 第一阶段结束：待确认文件进入对话式确认流程（文字摘要 + 回复确认）；
                // 自动确认模式下无待确认文件，直接由 WS 推进 Phase 2 并触发流式回答
                updateMessage(aiMessageId, {
                    stepsDoneAt: Date.now(), // 停止耗时计时
                    notice: '',
                    text: filesNeedingConfirmation.length > 0
                        ? '✅ 文件解析完成！请根据下方提取摘要进行确认，确认后我将为您进行深度 NTRK 审核分析。'
                        : autoConfirmOn
                            ? '✅ 文件解析完成，已自动进入 NTRK 深度分析，请稍候...'
                            : '✅ 文件解析完成！',
                });
                if (filesNeedingConfirmation.length > 0) {
                    confirmQueueRef.current = filesNeedingConfirmation;
                    startNextConfirm();
                }

                // 2026-09-22 兜底（SHPO 案实证）：自动确认模式下流式回答只靠 WS 完成事件
                // 触发——长分析期间 WS 静默死亡则事件丢失，结果永不出现也不落库，
                // 用户只能刷新靠历史回填看到。轮询兜底：全部完成且仍未触发则直接触发。
                // 停止条件：已触发/全部完成/全部失败取消/用户切走会话（刷新恢复链会承接），
                // 60 分钟兜底自停（真实样本最长达 15min 级，15min 上限不够，0922 用户抓出）。
                if (filesNeedingConfirmation.length === 0 && autoConfirmOn) {
                    const watcher = setInterval(async () => {
                        try {
                            if (streamTriggeredRef.current) { clearInterval(watcher); return; }
                            if (useChatStore.getState().currentSessionId !== sessionId) { clearInterval(watcher); return; }
                            const stats = await Promise.all(backendFileIds.map(fid =>
                                fileService.getFileStatus(fid, authString).catch(() => null)));
                            if (!stats.length || stats.some(s => !s)) return;
                            if (stats.every(s => ['completed', 'ntrk_analysis_completed'].includes((s as any).status))) {
                                if (streamTriggeredRef.current) { clearInterval(watcher); return; }
                                streamTriggeredRef.current = true; // 先置标志，与 WS 路径互斥
                                clearInterval(watcher);
                                pendingAnalysisFileIdsRef.current = [];
                                setPendingUserPrompt(null);
                                await triggerStreamingAnalysis(finalPrompt, backendFileIds);
                            } else if (stats.every(s => ['failed', 'cancelled'].includes((s as any).status))) {
                                clearInterval(watcher); // 失败/取消由既有消息链承接
                            }
                        } catch { /* 单轮异常下轮再试 */ }
                    }, 10000);
                    setTimeout(() => clearInterval(watcher), 60 * 60 * 1000);
                }

                // 重要：添加文件信息到消息中
                updateMessage(userMsgId, { files: filesToUpload });

                // 释放输入锁定：后续等待用户在确认弹窗中操作，
                // 期间用户仍可继续提问；确认完成后 triggerStreamingAnalysis
                // 会自行重新设置 isTyping(true)
                setIsTyping(false);
                setProcessingFileIds([]);
                return;
            }

            // --- 场景 B: 纯文字对话 ---
            
            await chatService.sendStreamMessage(
                {
                    message: finalPrompt,
                    // ✅ 使用局部变量 sessionId：currentSessionId 是闭包旧值，
                    // 新建会话后它仍是 null，会导致后端再建一个会话、消息丢失
                    session_id: sessionId,
                    file_ids: [],
                },
                authString!,
               (chunk) => {
                   
                    appendMessageText(aiMessageId, chunk,false); 
                  
                },
              
                (startData) => {
                    // --- 这里的逻辑就是你想要的 ---
                    // 1. 当收到后端确认开始的信号时，立即清空“正在准备...”等临时文字
                    updateMessage(aiMessageId, { text: '' });

                    // 2. 如果后端返回的会话与当前不同步（例如新建会话），以其为准
                    if (startData.sessionId && startData.sessionId !== sessionId) {
                        setCurrentSessionId(startData.sessionId);
                    }
                },

                (data) => {
                   if (data.sessionName) updateSessionName(data.sessionId, data.sessionName)
                   // 2026-09-18 用户令：挂服务端消息 ID（反馈/赞踩持久化用）
                   if (data.messageId) updateMessage(aiMessageId, { serverId: data.messageId } as any);

                   // 后端识别为报告文本：已进入 Graph 提取流程。
                   // 记录原始问题为 pendingUserPrompt，确认变异后自动触发流式分析回答
                   if (data.reportDetected && data.reportFileId) {
                       handledFileIdsRef.current.add(data.reportFileId);
                       pendingAnalysisFileIdsRef.current = [data.reportFileId];
                       pendingFileCountRef.current = 1;
                       streamTriggeredRef.current = false;
                       phase1CompletedRef.current = false;
                       setPendingUserPrompt(finalPrompt);
                       console.log('📋 报告文本已转入提取流程:', data.reportFileId);
                       // 文本链同样分钟级耗时（2026-09-17 文本走生产链后）：
                       // 挂步骤流+取消钮+耗时，与文件上传体验一致
                       const fid = data.reportFileId;
                       updateMessage(aiMessageId, {
                           stepStartedAt: Date.now(),
                           onCancel: () => {
                               fileService.cancelFile(fid, authString!).catch(e => console.warn('取消失败:', e));
                               updateMessage(aiMessageId, { notice: '取消中…' });
                           }
                       } as any);
                       appendProcessStep(aiMessageId, '报告文本已转入 NTRK 审核流程（与上传报告同一链路）');
                       fileService.waitForFilesProcessing(
                           [fid], authString!,
                           (msg, update) => appendProcessStep(aiMessageId, msg, update),
                           sessionId!, 15
                       ).then(() => {
                           updateMessage(aiMessageId, { stepsDoneAt: Date.now(), notice: '' });
                       }).catch(e => console.warn('文本链步骤等待异常（不阻塞 WS 驱动）:', e));
                   }
                }
            );


        } catch (error) {
            const msg = error instanceof Error ? error.message : '发送失败';
            
            // 清除超时提示
            if (fileProcessingTimeout) {
                clearTimeout(fileProcessingTimeout);
                fileProcessingTimeout = null;
            }

            if (isFileProcessing) {
                if (/超时/.test(msg)) {
                    // 前端等待超时 ≠ 后端失败：后台任务仍在执行，结果由常驻 WS 监听自动推送
                    appendProcessStep(aiMessageId, '解析耗时较长，后台仍在处理，完成后将自动推送结果');
                    updateMessage(aiMessageId, { notice: '' });
                    return; // 不收尾 stepsDoneAt：流程仍由 WS 事件驱动，计时继续走
                }
                // 文件处理失败：步骤框记红收尾 + 气泡给出重试指引
                appendProcessStep(aiMessageId, `文件处理失败：${msg}`, { status: 'failed' } as FileStatusUpdate);
                updateMessage(aiMessageId, { stepsDoneAt: Date.now(), notice: '' });
                updateAiMessage(aiMessageId, `❌ 文件处理失败: ${msg}\n\n如需重试，请重新上传文件。`);
            } else {
                updateAiMessage(aiMessageId, `抱歉，处理出错: ${msg}`);
            }
            setIsTyping(false); // 出错时停止动画
        } finally {
            // 改进：无论是否有文件，都要重置基础状态
            // 但文件上传场景下，isTyping 状态由 WebSocket 事件管理
            if (!isFileProcessing) {
                setIsTyping(false);
            }
            setProcessingFileIds([]);
        }
    }, [canSendMessage, createChatSession, addMessage, clearFiles, setIsTyping, updateAiMessage, updateSessionName, authString, files, isAuthenticated, currentSessionId, setCurrentSessionId, updateMessage, appendMessageText, appendProcessStep, handleConfirmReply, startNextConfirm]);
    // --- 会话管理 ---
    //need to review the logic of this function, as it is called when the user clicks on a session, but it also needs to be called after sending a message to update the session list and show the new session
    const triggerStreamingAnalysis = useCallback(async (prompt: string, fileIds: string[]) => {
        setIsTyping(true);
        const aiMessageId = Date.now();
        const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        // 在聊天记录中插入一条空的 AI 消息占位
        addMessage({ 
            id: aiMessageId, 
            text: '', 
            sender: 'ai', 
            timestamp, 
            
            liked: null 
        });

        try {
            console.log('🚀 开始流式分析:', { prompt, fileIds });
            // 调用流式服务
            await chatService.sendStreamMessage(
                { 
                    message: prompt, 
                    session_id: currentSessionId!, 
                    file_ids: fileIds,
                },
                authString!,
                (chunk) => {
                    console.log(`🎯 收到 chunk: "${chunk}"`);
                    appendMessageText(aiMessageId, chunk,false);
                },
                (startData) => {
                    // --- 这里的逻辑就是你想要的 ---
                    // 1. 当收到后端确认开始的信号时，立即清空“正在准备...”等临时文字
                    // 2026-09-17 友好性⑤⑥：生产同源 finalOut 直放时附带定性/警告元数据 → VerdictCard
                    updateMessage(aiMessageId, {
                        text: '',
                        verdict: startData.verdict || undefined,
                        reviewNote: startData.review_note || undefined,
                        partnerNote: startData.partner_note || undefined,
                        deterministic: !!startData.deterministic,
                    });
                    detVerdictRef.current = startData.deterministic ? (startData.verdict || '') : null;
                    
                    // 2. 如果是新会话，同步 Session ID
                    if (startData.sessionId && !currentSessionId) {
                        setCurrentSessionId(startData.sessionId);
                    }
                },
                async (data) => {
                    console.log('🎯 流式分析完成:', data);
                    if (data.sessionName) {
                        updateSessionName(data.sessionId, data.sessionName);
                    }
                    // 友好性⑦：生产同源直放完成后，拉取留痕文件折叠块挂到该消息
                    if (detVerdictRef.current !== null && fileIds.length > 0) {
                        try {
                            const arts = await fileService.getArtifacts(fileIds[0], authString!);
                            if (arts.length > 0) updateMessage(aiMessageId, { artifacts: arts });
                        } catch (e) { console.warn('留痕拉取失败（不阻塞）:', e); }
                    }
                    detVerdictRef.current = null;
                }
            );
        } catch (error) {
            console.error('流式分析失败:', error);
            updateAiMessage(aiMessageId, '抱歉，深度分析过程中出现了通讯异常。');
        } finally {
            setIsTyping(false);
        }
    }, [currentSessionId, authString, addMessage, updateAiMessage, setIsTyping, updateSessionName]);
  
    useEffect(() => {
        if (!pendingUserPrompt) return;
        const unsubscribe = fileService.onStatusUpdate(async (data) => {
            // 打印日志，确认数据是否进入了这里
            console.log("📩 收到文件状态更新:", data.status, "Session:", data.session_id);

            // 1. 校验 Session ID（仅当更新确实携带 session_id 时才校验；
            //    HTTP 轮询兜底推送的更新没有该字段，不应被丢弃）
            if (data.session_id && currentSessionId && data.session_id !== currentSessionId) {
                console.warn("⚠️ Session ID 不匹配，跳过逻辑");
                return;
            }

            // 2. 更新就绪状态
            if (data.file_summary) {
                setIsChatReady(data.file_summary.chat_ready);
                setFileStatus(data.file_summary);
            }

            // 2.5 提取结果快照（自动确认模式）：只读打印，替代人工确认环节
            if (data.extraction_result) {
                printExtractionSummary(data.extraction_result, data.file_id);
            }

            // 3. 多文件计数：每个文件完成时递减，收到 all_files_ready 直接归零
            if (data.status === 'ntrk_analysis_completed') {
                pendingFileCountRef.current = Math.max(0, pendingFileCountRef.current - 1);
            } else if (data.status === 'all_files_ready') {
                pendingFileCountRef.current = 0;
            }

            // 3.5 用户取消（友好性③）：中性提示收尾，不按失败渲染
            if (data.status === 'cancelled') {
                addMessage({
                    id: Date.now(),
                    text: `⏹ 已取消「${data.file_name || '检测报告'}」的分析。`,
                    sender: 'ai',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    liked: null,
                });
                setPendingUserPrompt(null);
                pendingFileCountRef.current = 0;
                setIsTyping(false);
                return;
            }

            // 4. Phase 2 失败：给出明确反馈并释放等待状态，避免用户无限等待
            //    （Phase 1 的 failed 由内联确认卡片承接，不在此提示）
            if (data.status === 'failed' && pendingUserPrompt && phase1CompletedRef.current) {
                console.error("❌ 文件分析失败:", data.status_message);
                addMessage({
                    id: Date.now(),
                    text: `❌ 文件「${data.file_name || '检测报告'}」的深度分析失败：${data.status_message || '未知错误'}\n\n您可以修正数据后重新确认，或直接向我提问。`,
                    sender: 'ai',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    liked: null,
                });
                setPendingUserPrompt(null);
                pendingFileCountRef.current = 0;
                setIsTyping(false);
                return;
            }

            // 5. 关键：所有文件都分析完成后才触发流式回答
            if ((data.status === 'ntrk_analysis_completed' || data.status === 'all_files_ready')
                && pendingFileCountRef.current === 0) {
                console.log("⭐ 所有文件分析完成，准备触发流式...");

                if (pendingUserPrompt && !streamTriggeredRef.current) {
                    // 同步置标志位 + 先消耗 prompt 再 await：
                    // 后端会连发 ntrk_analysis_completed 和 all_files_ready 两条消息，
                    // 若等 await 结束再清理，第二条消息会用旧闭包重复触发流式回答
                    streamTriggeredRef.current = true;
                    const prompt = pendingUserPrompt;
                    const linkedFileIds = pendingAnalysisFileIdsRef.current;
                    pendingAnalysisFileIdsRef.current = [];
                    setPendingUserPrompt(null);
                    console.log("🚀 执行流式回答:", prompt);
                    await triggerStreamingAnalysis(prompt, linkedFileIds);
                } else if (!pendingUserPrompt) {
                    console.warn("❌ 状态正确但没有 pendingUserPrompt，无法发起对话");
                }
            }
        });

        return () => unsubscribe();
    }, [currentSessionId, pendingUserPrompt, triggerStreamingAnalysis, addMessage, setIsTyping, printExtractionSummary]); //

    // 常驻订阅：未被上传/手动流程认领的文件状态更新（典型场景：聊天文本被识别为
    // 报告内容，后端完成提取后推送 to_be_confirmed），自动挂载内联确认卡片
    useEffect(() => {
        const unsubscribe = fileService.onStatusUpdate((data) => {
            if (!data.file_id || handledFileIdsRef.current.has(data.file_id)) return;
            if (data.session_id && currentSessionId && data.session_id !== currentSessionId) return;

            if (data.status === 'to_be_confirmed' || data.status === 'needs_review') {
                handledFileIdsRef.current.add(data.file_id);
                // 进入确认阶段后，再出现的 failed 属于 Phase 2 深度分析失败
                phase1CompletedRef.current = true;
                // 自动确认模式（默认，后端 auto_confirm 标志为 true）：上传/粘贴的报告
                // 由后台直接继续分析，前端只展示提取结果（见 pendingUserPrompt 效果中的
                // 快照打印），不进入确认交互。仅以下两种情况挂载对话式确认：
                // 1) needs_review：管线标记需人工审核，确认交互是补救入口；
                // 2) 服务端关闭自动确认（回滚，auto_confirm === false）。
                const mountConfirm = data.status === 'needs_review' || data.auto_confirm === false;
                if (mountConfirm && (!pendingConfirmRef.current || pendingConfirmRef.current.fileId !== data.file_id)) {
                    loadPendingConfirm(data.file_id, data.file_name || '输入的报告文本', data.status);
                }
            } else if (data.status === 'failed' && !phase1CompletedRef.current) {
                // 文本报告 Phase 1 提取失败（卡片尚未挂载，需在此反馈）
                handledFileIdsRef.current.add(data.file_id);
                addMessage({
                    id: Date.now(),
                    text: `❌ 报告文本解析失败：${data.status_message || '未能提取到有效的变异信息'}\n\n您可以补充更完整的报告内容后重试，或直接向我提问。`,
                    sender: 'ai',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    liked: null,
                });
                setPendingUserPrompt(null);
                pendingFileCountRef.current = 0;
                setIsTyping(false);
            }
        });
        return () => unsubscribe();
    }, [currentSessionId, addMessage, setIsTyping, loadPendingConfirm]);
    
    
    
    const fetchChatSessions = useCallback(async () => {
        if (!authString) return;
        setIsLoadingSessions(true);
        try {
            const sessions = await chatService.getChatSessions(authString);
            console.log('🔄 获取会话列表:', sessions);
            setChatSessions(sessions);
        } catch (error) {
            console.error('获取历史失败:', error);
        } finally {
            setIsLoadingSessions(false);
        }
    }, [authString, setIsLoadingSessions, setChatSessions]);
    //need to review the logic of this function, as it is called when the user clicks on a session, but it also needs to be called after sending a message to update the session list and show the new session
   
  

    const switchToSession = useCallback(async (sessionId: string, _sessionName: string) => {
        if (sessionId === currentSessionId) return;
        try {
            const msgs = await chatService.getSessionMessages(sessionId, authString || undefined);
            console.log('🔄 切换会话，获取消息:', msgs);
            const formatted = msgs.map((m: any) => ({
                ...m,
                serverId: typeof m.id === 'string' ? m.id : m.serverId,
                feedbackText: m.feedback_text ?? m.feedbackText ?? null,
                timestamp: new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            }));

            // 2026-09-18 用户令：历史会话展示原始文件名（生产 append_uploaded_files_to_history 同款）——
            // 服务端历史不含上传记录，按会话文件清单补 📎 气泡（文本录入类虚拟文件不算）。
            // 注意位置：贴在每个分析气泡的紧邻前位（生产同款：上传记录挨着它的分析），
            // 不能放对话顶部——长会话里用户视野在底部，顶部气泡等于没有（0918 用户实测"没看到"）
            let sfiles: any[] = [];
            try { sfiles = await fileService.getSessionFiles(sessionId, authString!); } catch { sfiles = []; }
            const namedFiles = sfiles.filter(f => f.file_name &&
                !f.file_name.startsWith('手动录入') && !f.file_name.startsWith('输入文本'));
            const ts0 = formatted[0]?.timestamp || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            const fileBubbleFor = (f: any) => {
                const names: string[] = [f.file_name, ...(f.attached_names || [])];
                return {
                    id: Date.now() - 1 - Math.floor(Math.random() * 1000),
                    text: `📎 原始上传文件（${names.length} 个）\n${names.map(n => '📄 ' + n).join('\n')}`,
                    sender: 'user' as const,
                    timestamp: ts0,
                    liked: null,
                };
            };

            // 2026-09-18 用户令：空会话不再造"欢迎回到"占位气泡——
            // 空态交给引导卡（messages 无用户消息即空态），取消/失败案由终态回放补
            setMessages(formatted);
            setCurrentSessionId(sessionId);

            // ── 友好性④ 刷新恢复（对齐离线版 resumeTaskIfRunning）──
            // 会话里还有进行中的文件 → 重建进度消息并续等；等完直接把已落库的
            // 生产同源结果（structured_summary.display_text）补进消息流——
            // 刷新期间文件在后台完成时无人触发流式回答，结果靠这里补显。
            try {
                const inflight = sfiles.filter(f => ['uploaded', 'processing', 'ntrk_analysis'].includes(f.status));
                if (inflight.length > 0) {
                    const ids = inflight.map(f => f.id);
                    processingFileIdsRef.current = ids;
                    setProcessingFileIds(ids);
                    pendingFileCountRef.current = ids.length;
                    const aiMessageId = Date.now();
                    const ts = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    namedFiles.filter(f => ids.includes(f.id)).forEach(f => addMessage(fileBubbleFor(f) as any));
                    addMessage({ id: aiMessageId, text: '', sender: 'ai', timestamp: ts, liked: null });
                    appendProcessStep(aiMessageId, '检测到进行中的分析任务，已为你恢复等待…');
                    updateMessage(aiMessageId, {
                        onCancel: () => {
                            ids.forEach(fid => fileService.cancelFile(fid, authString!).catch(e => console.warn('取消失败:', e)));
                            updateMessage(aiMessageId, { notice: '取消中…' });
                        }
                    });
                    await fileService.waitForFilesProcessing(
                        ids, authString!,
                        (msg, update) => appendProcessStep(aiMessageId, msg, update),
                        sessionId, 15);
                    // 等完补显结果（display_text 已在后端落库，直接渲染，不再过 LLM）
                    for (const fid of ids) {
                        try {
                            const st = await fileService.getFileStatus(fid, authString!);
                            const ss: any = (st as any).structured_summary;
                            const disp = ss?.display_text || ss?.ntrk_analysis?.display_text;
                            if (disp) {
                                const rid = Date.now() + Math.floor(Math.random() * 1000);
                                addMessage({
                                    id: rid, text: disp, sender: 'ai',
                                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                                    liked: null,
                                    verdict: ss?.verdict || ss?.ntrk_analysis?.verdict || undefined,
                                    reviewNote: ss?.review_note || undefined,
                                    partnerNote: ss?.partner_note || undefined,
                                    deterministic: true,
                                } as any);
                                try {
                                    const arts = await fileService.getArtifacts(fid, authString!);
                                    if (arts.length > 0) updateMessage(rid, { artifacts: arts });
                                } catch { /* 留痕失败不阻塞 */ }
                            } else if (st.status === 'failed' || st.status === 'cancelled') {
                                addMessage({
                                    id: Date.now() + 1,
                                    text: st.status === 'cancelled' ? '⏹ 已取消本次分析。' : `❌ 分析失败：${st.status_message || '未知错误'}`,
                                    sender: 'ai',
                                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                                    liked: null,
                                });
                            }
                        } catch (e) { console.warn('恢复结果补显失败:', e); }
                    }
                    updateMessage(aiMessageId, { stepsDoneAt: Date.now() } as any);
                    processingFileIdsRef.current = [];
                    setProcessingFileIds([]);
                    pendingFileCountRef.current = 0;
                }

                // ── 终态回放（2026-09-18 对齐生产 task_status，用户实测抓出）：
                // 取消/失败案历史里没有任何气泡（服务端不落 ChatMessage），
                // 重开会话时按 Redis 步骤流重建过程气泡 + ⏹/❌ 终态行。
                // 已完成案结论卡已在历史消息里（上面⑤⑥⑦回填），不重复回放。
                const deadFiles = sfiles.filter(f => f.status === 'cancelled' || f.status === 'failed');
                for (const f of deadFiles) {
                    try {
                        const st = await fileService.getFileStatus(f.id, authString!);
                        const steps: { m: string }[] = (st as any).steps || [];
                        const bid = Date.now() + Math.floor(Math.random() * 100000);
                        addMessage(fileBubbleFor(f) as any);
                        addMessage({
                            id: bid, text: '', sender: 'ai', liked: null,
                            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        });
                        steps.forEach(s => appendProcessStep(bid, s.m));
                        appendProcessStep(bid, f.status === 'cancelled'
                            ? '⏹ 已取消本次分析。'
                            : `❌ 分析失败：${st.status_message || '未知错误'}`);
                        updateMessage(bid, { stepsDoneAt: Date.now() } as any);
                    } catch (e) { console.warn('终态回放失败:', e); }
                }

                // ── 友好性⑤⑥⑦历史回填（2026-09-17）：已完成文件的生产同源结论元数据
                // 不在 Message 表里（历史只有纯文本），切会话时按 display_text 精确匹配
                // 历史气泡，回填 VerdictCard/警告列/留痕折叠块——对齐当次跑动的展示。
                const doneFiles = sfiles.filter(f =>
                    (f.status === 'completed' || f.status === 'ntrk_analysis_completed') && f.structured_summary);
                for (const f of doneFiles) {
                    const ss: any = f.structured_summary;
                    const disp = ss?.display_text || ss?.ntrk_analysis?.display_text;
                    if (!disp) continue;
                    const hit = useChatStore.getState().messages.find(m =>
                        m.sender === 'ai' && !m.deterministic && m.text && m.text.trim() === disp.trim());
                    if (!hit) {
                        // 2026-09-18（自查抓出）：分析在关页/非附着期间完成时，结论从未
                        // 落 ChatMessage（流式回答要前端附着才触发）——历史里没有任何气泡，
                        // 直接按库里的生产同源结果补建结论卡（对齐生产 append_history 服务端落库）
                        const rid = Date.now() + Math.floor(Math.random() * 100000);
                        addMessage(fileBubbleFor(f) as any);
                        addMessage({
                            id: rid, text: disp, sender: 'ai', liked: null,
                            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                            verdict: ss?.verdict || ss?.ntrk_analysis?.verdict || undefined,
                            reviewNote: ss?.review_note || undefined,
                            partnerNote: ss?.partner_note || undefined,
                            deterministic: true,
                        } as any);
                        try {
                            const arts = await fileService.getArtifacts(f.id, authString!);
                            if (arts.length > 0) updateMessage(rid, { artifacts: arts } as any);
                        } catch { /* 留痕回填失败不阻塞 */ }
                        continue;
                    }
                    // 📎 插到命中气泡紧邻前位（生产同款：上传记录挨着它的分析）
                    const _cur = useChatStore.getState().messages;
                    const _idx = _cur.findIndex(m => m.id === hit.id);
                    if (_idx >= 0 && !(_cur[_idx - 1]?.text || '').startsWith('📎 原始上传文件')) {
                        setMessages([..._cur.slice(0, _idx), fileBubbleFor(f) as any, ..._cur.slice(_idx)]);
                    }
                    updateMessage(hit.id, {
                        verdict: ss?.verdict || ss?.ntrk_analysis?.verdict || undefined,
                        reviewNote: ss?.review_note || undefined,
                        partnerNote: ss?.partner_note || undefined,
                        deterministic: true,
                    } as any);
                    try {
                        const arts = await fileService.getArtifacts(f.id, authString!);
                        if (arts.length > 0) updateMessage(hit.id, { artifacts: arts } as any);
                    } catch { /* 留痕回填失败不阻塞 */ }
                }
            } catch (e) {
                console.warn('恢复进行中任务失败（不阻塞）:', e);
            }
        } catch (error) {
            console.error('切换失败:', error);
        }
    }, [currentSessionId, authString, setMessages, setCurrentSessionId, addMessage, updateMessage, appendProcessStep, setProcessingFileIds]);

    const deleteChatSession = useCallback(async (sessionId: string, _sessionName: string) => {
        if (!authString) return;
        try {
            console.log('🔄 删除会话:', sessionId);
            await chatService.deleteSession(sessionId, authString);
            if (currentSessionId === sessionId) {
                setCurrentSessionId(null);
                clearChat();
            }
            fetchChatSessions();
        } catch (error) {
            console.error('删除会话失败:', error);
        }
    }, [authString, currentSessionId, setCurrentSessionId, clearChat, fetchChatSessions]);

    // 导出对象：修复了 removeFile 和 clearFiles 的缺失
    /**
     * 手动录入变异信息：创建虚拟文件并在消息流中挂载确认卡片，
     * 用户填写确认后与上传报告一样走 Phase 2 深度分析
     */
    const startManualEntry = useCallback(async () => {
        if (!isAuthenticated || !authString) return;
        const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        try {
            let sessionId = currentSessionId;
            if (!sessionId) {
                sessionId = await createChatSession();
                if (!sessionId) throw new Error('无法创建聊天会话，请稍后重试');
            }

            const res = await fileService.createManualEntry(sessionId, authString);

            // 认领该文件，并预备确认后的流式分析
            handledFileIdsRef.current.add(res.file_id);
            pendingAnalysisFileIdsRef.current = [res.file_id];
            pendingFileCountRef.current = 1;
            streamTriggeredRef.current = false;
            phase1CompletedRef.current = true; // 手动录入无 Phase 1，failed 均为 Phase 2
            setPendingUserPrompt('请基于我录入的 NTRK 变异信息，分析该样本的融合情况与 NTRK 审核适应性。');

            // 对话式录入引导：无提取数据，缺失项即全部必填信息
            pendingConfirmRef.current = {
                fileId: res.file_id,
                fileName: res.file_name,
                variants: [],
                tumorType: '',
                coVariants: '',
                missing: ['检测平台技术（如 NGS/PCR/FISH/IHC）', 'NTRK 变异名称（如 ETV6-NTRK3）', '断点/外显子信息（如 E5:E15）', '癌种（如 非小细胞肺癌）'],
                awaitingSkip: false,
                manualEntry: true,
            };

            // 2026-09-18 用户令：录入提醒列全要素（平台技术/机构/癌种/基因变异/丰度/转录本）
            addMessage({
                id: Date.now(),
                text: '✍️ 请直接发送变异信息，建议包含：\n· 检测平台技术（如 NGS/PCR/FISH/IHC）\n· 检测机构\n· 癌种（如 非小细胞肺癌）\n· 检测到的基因及具体变异信息（如 ETV6-NTRK3 融合，断点 E5:E15）\n· 丰度（如 15.6%）\n· 注释转录本（如 NM_002529）\n\n示例：NGS，XX医学检验所，非小细胞肺癌，ETV6-NTRK3 融合 E5:E15，丰度 15.6%，NM_002529\n\n我将解析并整理为结构化数据供您确认。',
                sender: 'ai',
                timestamp,
                liked: null,
            });
        } catch (error) {
            addMessage({
                id: Date.now(),
                text: `❌ 创建手动录入失败：${error instanceof Error ? error.message : '未知错误'}`,
                sender: 'ai',
                timestamp,
                liked: null,
            });
        }
    }, [isAuthenticated, authString, currentSessionId, createChatSession, addMessage]);

    // 重命名会话（2026-09-17：后端 PATCH /chat-sessions/{id}/ 原生支持）
    const renameChatSession = useCallback(async (sessionId: string, newName: string) => {
        const name = (newName || '').trim();
        if (!authString || !name) return;
        try {
            await chatService.renameSession(sessionId, name, authString);
            updateSessionName(sessionId, name);
        } catch (error) {
            console.error('重命名会话失败:', error);
        }
    }, [authString, updateSessionName]);

    return {
        messages, isTyping, files, currentSessionId, chatSessions, isLoadingSessions,
        hasMessages, canSendMessage, processingFileIds, connectionStatus,
        fileStatus, isChatReady, // 新增文件状态管理
        sendMessage,
        startManualEntry,
        handleFileSelect,
        removeFile: handleRemoveFile, // 导出修复项
        clearFiles: handleClearFiles,   // 导出修复项
        fetchChatSessions,
        switchToSession,
        deleteChatSession,
        renameChatSession,
        updateMessage,   // 2026-09-18 反馈/赞踩持久化后回写消息
        handleNewChat: useCallback(() => { 
            console.log('🔄 新建聊天');
            if(currentSessionId && hasMessages) fetchChatSessions(); 
            clearChat(); 
        }, [currentSessionId, hasMessages, fetchChatSessions, clearChat])
    };
}